import { Component, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject, map, of, startWith, switchMap, takeUntil } from 'rxjs';
import { NETWORK_SPECIALITIES, TITLES } from './../../../core/data/app.data';
import { ColleagueService } from './../../../core/services/colleague.service';
import { FeedbackService } from './../../../core/services/feedback.service';
import { PatientService } from './../../../core/services/patient.service';

interface EmailRecipient {
  name: string;
  email: string;
}

@Component({
  selector: 'app-bulk-email-list-creator',
  templateUrl: './bulk-email-list-creator.component.html',
  styleUrls: ['./bulk-email-list-creator.component.scss']
})
export class BulkEmailListCreatorComponent implements OnDestroy {

  protected destroy$ = new Subject<void>();


  medicalTeam: any[] = [];
  recipientList: EmailRecipient[] = [];
  colleagues: any[] = [];
  filteredColleagues$!: Observable<any[]>;

  colleagueName = new FormControl('');
  colleagueEmail = new FormControl('');

  addingColleague: any = {}; // Object to store the new colleague being added
  @ViewChild('addColleagueDialog') addColleagueDialog?: TemplateRef<any>;
  titles = TITLES;
  specialities = NETWORK_SPECIALITIES;

  constructor(private patientService: PatientService, private colleagueService: ColleagueService, private feedbackService: FeedbackService, private dialog: MatDialog) {

    this.patientService.currentPatient$
      .pipe(takeUntil(this.destroy$))
      .subscribe(selectedPatient => {
        if (selectedPatient.medicalTeam) {
          this.medicalTeam = selectedPatient.medicalTeam;
        }
      });

    this.colleagueService.colleagues$
      .pipe(takeUntil(this.destroy$))
      .subscribe(colleagues => {
        if (colleagues) {
          this.colleagues = colleagues;
        }
      });


    this.filteredColleagues$ = this.colleagueName.valueChanges.pipe(
      startWith(''),
      switchMap(value => this.filterColleagues(value!))
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  canAddTeamMember(email: string) {
    if (!email) return false;
    if (this.recipientList?.map((rec: any) => rec.email)?.indexOf(email) !== -1) return false;
    return true;
  }

  addTeamMember(event: MouseEvent | null, member: any) {
    event?.preventDefault();
    event?.stopPropagation();
    const name = `${member.title ? member.title + ' ' : ''}` + member.name + `${member.surname ? ' ' + member.surname : ''}`
    this.recipientList.push({ name, email: member.email });
    this.colleagueName.setValue('');
    this.colleagueEmail.setValue('');
  }

  removeRecipient(recipient: any) {
    const index = this.recipientList.indexOf(recipient);
    if (index >= 0) {
      this.recipientList.splice(index, 1);
    }
  }

  async checkAddColleague() {
    const name = this.colleagueName.value;
    const email = this.colleagueEmail.value;

    if (name && email) {
      const existingColleague = this.colleagues.find(c => `${c.title} ${c.name} ${c.surname}` === name && c.email === email);

      if (!existingColleague) {
        const addColleague = await this.feedbackService.showConfirmation('Add New Colleague?', 'This colleague does not exist in your colleague list. Would you like to add them now?', [], 'No', 'Yes');

        if (addColleague) {
          await this.addNewColleague(name, email);
        }

      }

      const existingRecipient = this.recipientList.find(c => c.name === name && c.email === email)
      if (!existingRecipient) {
        this.addTeamMember(null, { name, email });
      }
    }
  }


  private async addNewColleague(name: string, email: string): Promise<void> {
    return new Promise((resolve) => {
      this.addingColleague = { title: '', name, surname: '', speciality: '', email, contactNumber: '' };

      const dialogRef = this.dialog.open(this.addColleagueDialog!, { width: '600px' }); // Open the add colleague dialog

      dialogRef.afterClosed().subscribe(colleagueDetails => {
        if (colleagueDetails) {
          this.colleagueService.addColleague(this.addingColleague)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
              (response: any) => {
                this.feedbackService.showToast('Colleague added successfully');
                resolve();
              },
              (error) => {
                console.error('Error adding colleague:', error);
                this.feedbackService.showToast('There was an error adding the colleague. Please try again.');
                resolve();
              }
            );
        }
      })
    });
  }

  selectColleague(colleague: any) {
    if (colleague && colleague.email) {
      this.recipientList.push({ name: `${colleague.title} ${colleague.name} ${colleague.surname}`, email: colleague.email });
      this.colleagueName.setValue('');
      this.colleagueEmail.setValue('');
    }
  }

  private filterColleagues(value: string): Observable<any[]> {
    if (!value || typeof value !== 'string') return of([]);
    const filterValue = value.toLowerCase();
    return of(this.colleagues).pipe(
      map(colleagues => colleagues.map(colleague => {
        colleague.fullName = `${colleague.title} ${colleague.name} ${colleague.surname}`;
        return colleague;
      }).filter(colleague =>
        colleague.fullName.toLowerCase().includes(filterValue) ||
        colleague.email.toLowerCase().includes(filterValue)
      ))
    );
  }

}
